import { useMemo, useState } from "react";
import omit from "lodash/omit";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { parse } from "qs";

import { Placement } from "@/api/requests/getBrandPlacement";
import { DealBanner } from "@/api/requests/getDealBanner";
import {
  DEFAULT_TAKE_COUNT,
  GetMenuItemsReturn,
} from "@/api/requests/getMenuItems";
import { DISPENSARY_MEDICAL_DISCLAIMER } from "@/constants/disclaimers";
import { Deal } from "@/custom-types/Deals";
import { Dispensary } from "@/custom-types/Dispensary";
import { MedicalIdCondition } from "@/custom-types/MedicalIdCondition";
import { MenuItem } from "@/custom-types/MenuItem";
import { COMMON_SEARCH_PARAMS, IGNORED_QUERIES } from "@/hooks/useFilters";
import { leaflyShopStringify } from "@/utils/qs";

import DesktopFilterColumnUI from "@/components/DesktopFilterColumnUI";
import ActiveDealsCarousel from "@/components/Dispensary/ActiveDealsCarousel";
import CategoryCarousel from "@/components/Dispensary/CategoryCarousel";
import MedicalInfo from "@/components/Dispensary/MedicalInfo";
import PromotedBrandCarousel from "@/components/Dispensary/PromotedBrandCarousel";
import SearchBar from "@/components/Dispensary/SearchBar";
import DynamicFilters from "@/components/DynamicFilters";
import { AvailableFilter } from "@/components/DynamicFilters/types";
import MedicalDisclaimers from "@/components/MedicalDisclaimers";
import MobileFilterModalUI, {
  MobileFilterModalProvider,
} from "@/components/MobileFilterModalUI";
import PageNavigation from "@/components/PageNavigationV2";
import SearchAd from "@/components/SearchAd";
import AppliedChips from "@/components/UnifiedShop/AppliedChips";
import MobileMenuFilterShortcuts from "@/components/UnifiedShop/MobileMenuFilterShortcuts";

import { DealsBanner } from "../DealsBanner/DealsBanner";
import DispensaryMenuItems from "../DispensaryMenuItems";
import LastUpdatedTime from "../LastUpdatedTime";
import MenuSortDropDown from "../MenuSortDropDown";
import StaffPicksCarousel from "../StaffPicksCarousel";
import StrainEffectCopy from "./StrainEffectCopy";

const MenuContainer: React.FC<{
  brandPlacement: Placement | null;
  deals: Deal[];
  dealsBanner: DealBanner | null;
  dispensary: Dispensary;
  menuData: GetMenuItemsReturn;
  staffPicks: MenuItem[];
}> = ({
  brandPlacement,
  deals,
  dealsBanner,
  dispensary,
  menuData,
  staffPicks,
}) => {
  const [openFilterSection, setOpenFilterSection] = useState<string>();

  const { push } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const parsedQuery = parse(searchParams?.toString() || "");

  const { availableFilters, availableSorts, menuItems, totalItems } = menuData;

  const filtersApplied =
    Object.keys(omit(parsedQuery, [...IGNORED_QUERIES, "menu_type"])).length >
    0;
  const commonSearchParamsApplied = COMMON_SEARCH_PARAMS.some(
    (key) => key in parsedQuery,
  );

  const clearAllFilterParams = new URLSearchParams(searchParams?.toString());
  clearAllFilterParams.delete("page");
  availableFilters.forEach((filter) => {
    clearAllFilterParams.delete(filter.name);
  });
  const clearAllFilterParamsQueryString = clearAllFilterParams.toString();

  const hasActiveFilter = filtersApplied || commonSearchParamsApplied;

  const totalPages = useMemo(
    () => Math.ceil((totalItems || 0) / DEFAULT_TAKE_COUNT),
    [totalItems],
  );

  const isCanadianDispensary = dispensary.country === "CA";

  const isMedIdRequired =
    dispensary.orderMedIdCondition === MedicalIdCondition.Required;

  const page = parseInt(parsedQuery.page ? String(parsedQuery.page) : "1", 10);

  return (
    <div className="pb-section">
      <div className="container pt-lg">
        <SearchBar
          className="mt-md mb-lg"
          dispensaryId={dispensary.id}
          dispensaryTags={dispensary.tags}
        />
        {isMedIdRequired && (
          <MedicalInfo
            dispensaryId={dispensary.id}
            dispensaryRetailType={dispensary.retailType}
            dispensaryTags={dispensary.tags}
          />
        )}
      </div>
      <div className="mt-md">
        <LastUpdatedTime
          totalItems={totalItems}
          lastMenuUpdate={dispensary.lastMenuUpdate}
          className="pb-sm container"
        />
        <section className="container" data-testid="filter-layout">
          <div className="grid grid-cols-1 gap-1 md:gap-8 md:grid-cols-4 md:m-0">
            <div>
              <DesktopFilterColumnUI
                clearAllHref={{
                  pathname,
                  query: clearAllFilterParamsQueryString,
                }}
                chipsUI={
                  <AppliedChips
                    availableFilters={availableFilters as AvailableFilter[]}
                  />
                }
                filtersUI={
                  <DynamicFilters
                    availableFilters={availableFilters as AvailableFilter[]}
                    defaultOpenKey={openFilterSection}
                  />
                }
              />
              <MobileFilterModalProvider>
                <MobileFilterModalUI
                  clearAllHref={{
                    pathname,
                    query: clearAllFilterParamsQueryString,
                  }}
                  numberOfFiltersSelected={0}
                  setOpenFilterSection={setOpenFilterSection}
                  chipsUI={
                    <>
                      <AppliedChips
                        availableFilters={availableFilters as AvailableFilter[]}
                      />
                      <MobileMenuFilterShortcuts
                        availableFilters={availableFilters as AvailableFilter[]}
                        setOpenFilterSection={setOpenFilterSection}
                      />
                    </>
                  }
                  filtersUI={
                    <DynamicFilters
                      availableFilters={availableFilters as AvailableFilter[]}
                      defaultOpenKey={openFilterSection}
                      sectionPadding="max-md:px-4"
                    />
                  }
                />
              </MobileFilterModalProvider>
            </div>

            <div className="md:col-span-3">
              {isCanadianDispensary && (
                <SearchAd
                  className="my-xl"
                  page="store"
                  tag="menu_upper"
                  customTargeting={{ dispensary: dispensary.slug }}
                />
              )}
              <MenuSortDropDown
                availableSorts={availableSorts}
                className="mb-md"
                totalItems={totalItems}
              />
              {!hasActiveFilter && (
                <CategoryCarousel
                  dispensaryRetailType={dispensary.retailType}
                  dispensarySlug={dispensary.slug}
                  className="mb-lg"
                  availableFilters={availableFilters as AvailableFilter[]}
                />
              )}
              <DealsBanner banner={dealsBanner} />
              <PromotedBrandCarousel
                brandPlacement={brandPlacement}
                cardsPerSlide={3}
                className="mb-section mt-xl"
                dispensary={dispensary}
              />
              {!hasActiveFilter && (
                <div data-testid="merchandising-tray">
                  <ActiveDealsCarousel
                    cardsPerSlide={3}
                    className="mb-section mt-xl"
                    deals={deals}
                    dispensary={dispensary}
                  />
                  <StaffPicksCarousel
                    cardsPerSlide={3}
                    className="mb-section mt-xl"
                    dispensaryId={dispensary.id}
                    dispensaryRetailType={dispensary.retailType}
                    dispensarySlug={dispensary.slug}
                    maxCardCount={6}
                    staffPicks={staffPicks}
                  />
                </div>
              )}
              <StrainEffectCopy menuItems={menuData.menuItems} />
              <DispensaryMenuItems
                hasActiveFilter={hasActiveFilter}
                menuItems={menuItems as MenuItem[]}
                loading={false}
                onResetFiltersClick={() => {
                  push(
                    `${pathname}${clearAllFilterParamsQueryString ? `?${clearAllFilterParamsQueryString}` : ""}`,
                  );
                }}
              />
              <PageNavigation
                className="my-xxl"
                currentPage={page}
                stringifyMethod={leaflyShopStringify}
                totalPages={totalPages}
              />
            </div>
          </div>
        </section>
        <MedicalDisclaimers
          usDisclaimer={DISPENSARY_MEDICAL_DISCLAIMER}
          className="container mt-xxl"
        />
      </div>
    </div>
  );
};

export default MenuContainer;
